// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import { hooks as colocatedHooks } from "phoenix-colocated/filterino";
import topbar from "../vendor/topbar";
import live_select from "live_select";
// Hooks
import BlockTextArea from "./hooks/block_text_area";
import SortableHook from "./hooks/sortable_hook";
import ColorPickerHook from "./hooks/color_picker_hook";
import RangeValueHook from "./hooks/range_value_hook";
import FocusHook from "./hooks/focus_hook";
import SearchFocusHook from "./hooks/search_focus_hook";
import ScrollPulseHook from "./hooks/scroll_pulse_hook";
import NumericInputHook from "./hooks/numeric_input_hook";
import { AudioPreviewHook } from "./hooks/audio_preview_hook";
import { GlobalAudioHook } from "./hooks/global_audio_hook";
import { GlobalHook } from "./hooks/global_hook";
import { PreviewLayoutHook } from "./hooks/preview_layout_hook";
import ScrollTrackingHook from "./hooks/scroll_tracking_hook";
import FlashAutoCloseHook from "./hooks/flash_auto_close_hook.js";
import SearchShortcutHook from "./hooks/search_shortcut_hook.js";
import SearchResultsHook from "./hooks/search_results_hook.js";
import SaveButtonHook from "./hooks/save_button_hook.js";
import FontSizeSliderHook from "./hooks/font_size_slider_hook.js";
import LocalTimeHook from "./hooks/local_time_hook.js";
import { shouldShowGoToTopButton } from "./go_to_top.js";
let csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content");

let Hooks = {
  SortableHook: SortableHook,
  BlockTextArea: BlockTextArea,
  ColorPickerHook: ColorPickerHook,
  RangeValueHook: RangeValueHook,
  FocusHook: FocusHook,
  SearchFocusHook: SearchFocusHook,
  ScrollPulseHook: ScrollPulseHook,
  NumericInputHook: NumericInputHook,
  AudioPreviewHook: AudioPreviewHook,
  GlobalAudioHook: GlobalAudioHook,
  GlobalHook: GlobalHook,
  PreviewLayoutHook: PreviewLayoutHook,
  ScrollTrackingHook: ScrollTrackingHook,
  FlashAutoCloseHook: FlashAutoCloseHook,
  SearchShortcutHook: SearchShortcutHook,
  SearchResultsHook: SearchResultsHook,
  SaveButtonHook: SaveButtonHook,
  FontSizeSliderHook: FontSizeSliderHook,
  LocalTimeHook: LocalTimeHook,
  ...colocatedHooks,
  ...live_select,
};
let liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 2500,
  params: { _csrf_token: csrfToken },
  hooks: Hooks,
});

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" });
window.addEventListener("phx:page-loading-start", (_info) => topbar.show(300));
window.addEventListener("phx:page-loading-stop", (_info) => topbar.hide());

// connect if there are any LiveViews on the page
liveSocket.connect();

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;

// Handle file downloads
// Legacy download handler (kept for any push_event download usage)
window.addEventListener("phx:download", (event) => {
  const { filename, content, mime_type, encoding } = event.detail;

  let blobData;

  if (encoding === "base64") {
    const byteCharacters = atob(content);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    blobData = new Uint8Array(byteNumbers);
  } else {
    blobData = content;
  }

  const blob = new Blob([blobData], { type: mime_type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
});

// Theme toggle functionality
document.addEventListener("DOMContentLoaded", function () {
  // Get saved theme from localStorage or default to 'halloween'
  let savedTheme = localStorage.getItem("theme") || "halloween";
  // Migrate users from the old light theme name.
  if (savedTheme === "retro") {
    savedTheme = "fantasy";
    localStorage.setItem("theme", savedTheme);
  }
  document.documentElement.setAttribute("data-theme", savedTheme);
  updateThemeIcon(savedTheme);

  // Handle theme toggle buttons
  document.addEventListener("click", function (e) {
    if (e.target.classList.contains("theme-toggle")) {
      const theme = e.target.getAttribute("data-set-theme");
      if (theme) {
        document.documentElement.setAttribute("data-theme", theme);
        localStorage.setItem("theme", theme);
        updateThemeIcon(theme);

        // Close the dropdown after selection
        const dropdown = e.target.closest(".dropdown");
        if (dropdown) {
          const details = dropdown.querySelector("[tabindex]");
          if (details) details.blur();
        }
      }
    }
  });

  // Update active navigation states
  updateActiveNavigation();
});

// Function to update theme icon based on current theme
function updateThemeIcon(theme) {
  const themeIcon = document.querySelector(".theme-toggle-icon");
  if (themeIcon) {
    // Remove all theme icon classes
    themeIcon.classList.remove("hero-sun", "hero-moon");

    // Add appropriate icon based on theme
    switch (theme) {
      case "halloween":
        themeIcon.classList.add("hero-moon");
        break;
      case "fantasy":
        themeIcon.classList.add("hero-sun");
        break;
      default:
        themeIcon.classList.add("hero-moon"); // Default to moon for halloween
    }
  }
}

// Function to update active navigation based on current path
function updateActiveNavigation() {
  const currentPath = window.location.pathname;
  const navLinks = document.querySelectorAll(".navbar a[href]");

  navLinks.forEach((link) => {
    const href = link.getAttribute("href");
    const isActive =
      (href === "/" && currentPath === "/") ||
      (href !== "/" && currentPath.startsWith(href));

    if (isActive) {
      link.classList.add("bg-primary/10", "text-primary");
      link.classList.remove("hover:bg-base-200");
    } else {
      link.classList.remove("bg-primary/10", "text-primary");
      link.classList.add("hover:bg-base-200");
    }
  });
}

// Update navigation on LiveView navigation
document.addEventListener("phx:navigate", updateActiveNavigation);

// Search functionality is now handled by phx-click events directly

// Go to Top Button functionality
document.addEventListener("DOMContentLoaded", function () {
  const goToTopBtn = document.getElementById("go-to-top-btn");

  if (goToTopBtn) {
    const mobileSectionsViewport = window.matchMedia("(max-width: 1279px)");

    // Show/hide button based on scroll position
    function toggleGoToTopButton() {
      const mobileSectionsMenu = document.getElementById(
        "mobile-sections-menu",
      );
      const shouldShow = shouldShowGoToTopButton({
        scrollOffset: window.pageYOffset,
        mobileSectionsMenuOpen: Boolean(mobileSectionsMenu?.open),
        mobileViewport: mobileSectionsViewport.matches,
      });

      if (shouldShow) {
        goToTopBtn.classList.remove("pointer-events-none");
        goToTopBtn.setAttribute("aria-hidden", "false");
        goToTopBtn.tabIndex = 0;
        goToTopBtn.style.opacity = "1";
        goToTopBtn.style.transform = "translateY(0)";
      } else {
        goToTopBtn.classList.add("pointer-events-none");
        goToTopBtn.setAttribute("aria-hidden", "true");
        goToTopBtn.tabIndex = -1;
        goToTopBtn.style.opacity = "0";
        goToTopBtn.style.transform = "translateY(20px)";

        if (document.activeElement === goToTopBtn) {
          goToTopBtn.blur();
        }
      }
    }

    // Listen for scroll events with throttling for better performance
    let ticking = false;
    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(toggleGoToTopButton);
        ticking = true;
        setTimeout(() => {
          ticking = false;
        }, 150);
      }
    }

    window.addEventListener("scroll", requestTick);
    window.addEventListener("resize", requestTick);

    // Handle click to scroll to top
    goToTopBtn.addEventListener("click", function () {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });

    // Listen for LiveView events that might cause scrolling
    document.addEventListener("phx:navigate", function () {
      // Check button visibility after navigation
      setTimeout(toggleGoToTopButton, 100);
    });

    document.addEventListener("phx:page-loading-stop", function () {
      // Check button visibility after page loading stops
      setTimeout(toggleGoToTopButton, 100);
    });

    // Listen for custom scroll events that might be triggered by LiveView
    document.addEventListener("scroll-and-pulse", function () {
      // Check button visibility after custom scroll events
      setTimeout(toggleGoToTopButton, 200);
    });

    document.addEventListener(
      "toggle",
      function (event) {
        if (event.target.id === "mobile-sections-menu") {
          toggleGoToTopButton();
        }
      },
      true,
    );

    // Periodic check to ensure button visibility is always correct
    // This handles cases where auto-scroll happens without triggering events
    setInterval(toggleGoToTopButton, 2000);

    // Initial check
    toggleGoToTopButton();
  }
});
